class CollectionFilter extends HTMLElement {
  constructor() {
    super();
    this.currentUrl = window.location.href;
    this.url = new URL(this.currentUrl);
    this.searchParams = this.url.searchParams;
    this.debouncedFilterProducts = this.debounce(
      this.filterProducts.bind(this),
      300
    );
    this.filterDrawerButton = this.querySelector(".filter-drawer-button");
    this.paginationLimit = parseInt(this.dataset.paginationLimit, 10);
    this.paginationElement = this.querySelector(".collection-pagination");
    this.collectionFooter = this.querySelector(".collection-footer");

    // Registry-specific properties
    this.customerId = document.body.dataset.customerId;
    this.hasRegistryFeature = this.hasAttribute("data-registry-enabled");
    this.hasWishlistFeature = this.hasAttribute("data-wishlist-enabled");
    this.registryCheckQueue = new Map();
    this.wishlistCheckQueue = new Map();
  }

  connectedCallback() {
    this.initializeElements();
    this.setupEventListeners();
    this.handleStickyCheck();
    this.setupPriceRangeListener();
    if (this.hasRegistryFeature && this.customerId) {
      this.initializeRegistryItems();
      this.setupRegistryListeners();
    }
  }

  initializeElements() {
    this.filtersSectionElement = this.querySelector(
      "#shopify-section-collection-filters"
    );
    this.main = this.querySelector(".contents");
    this.filtersSection = this.querySelector(".filters-panel-nav-content");
    this.filtersApply = this.filtersSection?.querySelector(`[type="submit"]`);
    this.filtersClear = this.filtersSection?.querySelector(`[type="reset"]`);
    this.filterRadios = this.querySelectorAll(
      ".filters-panel .filters-panel-nav input"
    );
    this.productGrid = this.querySelector("#collectionProductGrid");
    this.productGridCountFilterBar = this.querySelector(
      "#filteredProductCount"
    );
    this.productGridCountFooterPagination =
      this.querySelector("#productCountTotal");
    this.filterDrawerButtonProductCount = this.filtersSection?.querySelector(
      "#selectedFilterProductCount"
    );
    this.sortBy = this.querySelector("#sort-by");
    this.priceRange = this.querySelector("price-range");
  }

  setupEventListeners() {
    const debouncedHandleStickyCheck = this.debounce(
      this.handleStickyCheck.bind(this),
      7
    );
    window.addEventListener("scroll", debouncedHandleStickyCheck, {
      passive: true,
    });

    this.filterRadios.forEach((radio) => {
      radio.addEventListener("change", () => this.handleRadioChange(radio));
    });

    if (this.filtersApply) {
      this.filtersApply.addEventListener("click", (e) =>
        this.handleFiltersApply(e)
      );
    }

    if (this.filtersClear) {
      this.filtersClear.addEventListener("click", (e) =>
        this.handleFiltersClear(e)
      );
    }

    if (this.sortBy) {
      this.sortBy.addEventListener("change", () => this.handleSortChange());
    }

    this.setupA11yDialog();
    this.setupPriceRangeListener();
  }

  initializeRegistryItems() {
    this.registryCheckQueue.clear();

    // Queue up all registry checks
    const productTiles = this.querySelectorAll(".product-card");
    productTiles.forEach((tile) => {
      const productId = tile.dataset.productId;
      const addButton = tile.querySelector(".registry-add");
      const variantId = addButton?.dataset.variantId;

      if (productId && variantId) {
        const loadingButton = tile.querySelector(".registry-loading");
        if (loadingButton) loadingButton.style.display = "block";
        this.registryCheckQueue.set(productId, {
          variantId,
          element: tile,
        });
      }
    });

    this.processRegistryQueue();
  }

  async processRegistryQueue() {
    const batchSize = 5;
    const promises = [];
    let count = 0;

    for (const [productId, { variantId, element }] of this.registryCheckQueue) {
      promises.push(this.checkRegistryProduct(productId, variantId, element));
      this.registryCheckQueue.delete(productId);

      count++;
      if (count === batchSize) {
        await Promise.all(promises);
        count = 0;
        promises.length = 0;
      }
    }

    if (promises.length > 0) {
      await Promise.all(promises);
    }
  }

  async checkRegistryProduct(productId, variantId, productElement) {
    try {
      const response = await fetch(
        `/apps/registry/${readCookie('registry_slug')}/products/search?customer[shopify_customer_id]=${this.customerId}&product[shopify_product_id]=${productId}&product[shopify_variant_id]=${variantId}`
      );
      const data = await response.json();

      if (!productElement) return;

      const addButton = productElement.querySelector(".registry-add");
      const removeButton = productElement.querySelector(".registry-remove");
      const loadingButton = productElement.querySelector(".registry-loading");

      if (loadingButton) {
        loadingButton.style.display = "none";
      }

      if (data.success) {
        if (data.product_found) {
          if (addButton) addButton.style.display = "none";
          if (removeButton) {
            removeButton.style.display = "block";
            removeButton.dataset.registryProductId = data.product.id;
          }
        } else {
          if (addButton) addButton.style.display = "block";
          if (removeButton) removeButton.style.display = "none";
        }
      } else {
        if (addButton) addButton.style.display = "none";
        if (removeButton) removeButton.style.display = "none";
      }
    } catch (error) {
      console.error("Error checking registry product:", error);
      const loadingButton = productElement.querySelector(".registry-loading");
      if (loadingButton) loadingButton.style.display = "none";
    }
  }

  setupRegistryListeners() {
    this.addEventListener("click", async (event) => {
      const addButton = event.target.closest(".registry-add");
      const removeButton = event.target.closest(".registry-remove");

      if (addButton || removeButton) {
        event.preventDefault();

        const button = addButton || removeButton;
        const productElement = button.closest(".product-card");
        const loadingButton = productElement.querySelector(".registry-loading");

        if (loadingButton) {
          loadingButton.style.display = "block";
          button.style.display = "none";
        }

        if (addButton) {
          await this.handleAddToRegistry(addButton, productElement);
        } else {
          await this.handleRemoveFromRegistry(removeButton, productElement);
        }
      }
    });
  }

  // [NOTE] adding and removing to the registry app is handled in this legacy file: /registry-ajax-collection.js; lines 81 & 117

  async handleAddToRegistry(button, productElement) {
    const productId = button.dataset.productId;
    const variantId = button.dataset.variantId;

    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await this.checkRegistryProduct(productId, variantId, productElement);
    } catch (error) {
      console.error("Error adding to registry:", error);
    }
  }

  async handleRemoveFromRegistry(button, productElement) {
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const addButton = productElement.querySelector(".registry-add");
      const removeButton = productElement.querySelector(".registry-remove");
      const loadingButton = productElement.querySelector(".registry-loading");

      if (loadingButton) loadingButton.style.display = "none";
      if (removeButton) removeButton.style.display = "none";
      if (addButton) addButton.style.display = "block";
    } catch (error) {
      console.error("Error removing from registry:", error);
    }
  }

  revertLoadingState(productElement, button) {
    const loadingButton = productElement.querySelector(".registry-loading");
    if (loadingButton) {
      loadingButton.style.display = "none";
      button.style.display = "block";
    }
  }

  // Wishlist logic...

  initializeWishlistItems() {
    this.wishlistCheckQueue.clear();

    // Queue up all wishlist checks
    const productTiles = this.querySelectorAll(".product-card");
    productTiles.forEach((tile) => {
      const productId = tile.dataset.productId;
      if (productId) {
        this.wishlistCheckQueue.set(productId, {
          element: tile,
        });
      }
    });

    this.processWishlistQueue();
  }

  async processWishlistQueue() {
    const batchSize = 10;
    const promises = [];
    let count = 0;

    for (const [productId, { element }] of this.wishlistCheckQueue) {
      promises.push(this.checkWishlistProduct(productId, element));
      this.wishlistCheckQueue.delete(productId);

      count++;
      if (count === batchSize) {
        await Promise.all(promises);
        count = 0;
        promises.length = 0;
      }
    }

    if (promises.length > 0) {
      await Promise.all(promises);
    }
  }

  async checkWishlistProduct(productId, productElement) {
    try {
      const response = await fetch(
        `/apps/wishlist/search/?shop=${window.Shopify.shop}&customerId=${this.customerId}&productId=${productId}`
      );
      const data = await response.json();

      if (!productElement) return;

      const addButton = productElement.querySelector(".wishlist-add");
      const removeButton = productElement.querySelector(".wishlist-remove");

      if (data.exists) {
        removeButton?.classList.remove("hidden");
      } else {
        addButton?.classList.remove("hidden");
      }
    } catch (error) {
      console.error("Error checking wishlist product:", error);
      const addButton = productElement.querySelector(".wishlist-add");
      addButton?.classList.remove("hidden");
    }
  }

  setupWishlistListeners() {
    this.addEventListener("click", async (event) => {
      const addButton = event.target.closest(".wishlist-add");
      const removeButton = event.target.closest(".wishlist-remove");

      if (addButton || removeButton) {
        event.preventDefault();

        const button = addButton || removeButton;
        const productElement = button.closest(".product-card");

        button.classList.add("hidden");

        if (addButton) {
          await this.handleAddToWishlist(addButton, productElement);
        } else {
          await this.handleRemoveFromWishlist(removeButton, productElement);
        }
      }
    });
  }

  // [NOTE] adding and removing to the wishlist app is handled in this legacy file: /wishlist-ajax-collection.js; lines 48 & 73

  async handleAddToWishlist(button, productElement) {
    try {
      await new Promise((resolve) => resolve());
      const addButton = productElement.querySelector(".wishlist-add");
      const removeButton = productElement.querySelector(".wishlist-remove");

      removeButton?.classList.remove("hidden");
      addButton?.classList.add("hidden");
    } catch (error) {
      console.error("Error adding to wishlist:", error);
      button.classList.remove("hidden");
    }
  }

  async handleRemoveFromWishlist(button, productElement) {
    try {
      await new Promise((resolve) => resolve());
      const addButton = productElement.querySelector(".wishlist-add");
      const removeButton = productElement.querySelector(".wishlist-remove");

      removeButton?.classList.add("hidden");
      addButton?.classList.remove("hidden");
    } catch (error) {
      console.error("Error removing from wishlist:", error);
      button.classList.remove("hidden");
    }
  }

  setupPriceRangeListener() {
    if (this.priceRange) {
      this.priceRange.addEventListener("price-range-change", (event) => {
        this.handlePriceRangeChange(event.detail);
      });
    }
  }

  debounce(func, wait) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  handleStickyCheck() {
    if(navigator.maxTouchPoints < 1){ // only on non-touch devices - on touch this devices this had a wobbly effect after releasing a scroll
      const rect = this.filtersSectionElement?.getBoundingClientRect();
      const isPinned = rect?.top === 0;
      this.filtersSectionElement?.classList.toggle("is-pinned", isPinned);
    }
  }

  setupA11yDialog() {
    whenReady("A11yDialog", () => {
      const element = this.querySelector(".filters-panel");
      const dialog = new A11yDialog(element);
      const filterOptionButtons = this.querySelectorAll(
        ".filters__filter-options button"
      );

      filterOptionButtons.forEach((button) => {
        const handleInteraction = (event) => {
          this.selectedFilterId = event.currentTarget.getAttribute("data-id");
          if (this.selectedFilterId) {
            const targetDetails = element.querySelector(
              `details[data-id="${this.selectedFilterId}"]`
            );
            if (targetDetails) {
              targetDetails.setAttribute("open", "");
              targetDetails.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
              });
            }

            this.selectedFilterId = null;
          }
        };

        button.addEventListener("touchstart", handleInteraction, {
          passive: false,
        });
        button.addEventListener("click", handleInteraction);
      });

      dialog
        .on("show", () => {
          document.documentElement.classList.add("scroll-lock");
        })
        .on("hide", () => {
          document.documentElement.classList.remove("scroll-lock");
          this.scrollToContent();

          const allDetails = element.querySelectorAll("details");
          allDetails.forEach((details) => {
            details.removeAttribute("open");
          });
        });
    })
  }

  scrollToContent() {
    const filtersWrap = this.querySelector(".filters-wrap");
    if (this.main && filtersWrap) {
      window.scroll({
        top: this.main.offsetTop - filtersWrap.clientHeight - 1,
        left: 0,
        behavior: "smooth",
      });
    }
  }

  handleRadioChange(radio) {
    this.searchParams.delete(radio.name);

    this.querySelectorAll(
      `.filters-panel .filters-panel-nav input[name="${radio.name}"]`
    ).forEach((r) => {
      if (r.checked) {
        this.searchParams.append(radio.name, r.value);
      }
    });

    this.searchParams.delete("page");
    this.updateFilters();

    // Fetch the new filter state and update radio buttons and counts
    fetch(this.url.toString())
      .then((response) => response.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        // Update radio button and checkbox disabled state
        doc
          .querySelectorAll(".filters-panel .filters-panel-nav input")
          .forEach((input) => {
            const id = input.id;
            const localInput = document.getElementById(id);
            if (localInput) {
              localInput.disabled = input.disabled;
              if(localInput.disabled){
                localInput.parentElement.classList.add("checkbox-container--disabled");
              } else {
                localInput.parentElement.classList.remove("checkbox-container--disabled");
              }
            }
          });

        // Update the counts on the labels
        doc
          .querySelectorAll(".filters-panel .filters-panel-nav label")
          .forEach((label) => {
            const id = label.getAttribute("for");
            const localLabel = this.querySelector(`label[for="${id}"]`);
            if (localLabel) {
              localLabel.innerHTML = label.innerHTML;
            }
          });
      });
  }

  handleSortChange() {
    this.searchParams.delete("sort_by");
    this.searchParams.append("sort_by", this.sortBy.value);
    this.searchParams.delete("page");
    this.updateFilters();
  }

  handleFiltersApply(e) {
    e.preventDefault();
    const filtersPanel = this.querySelector(".filters-panel");
    if (filtersPanel) {
      filtersPanel.classList.remove("is-visible");
    }
  }

  handleFiltersClear(e) {
    e.preventDefault();

    this.querySelectorAll(
      `.filters-panel .filters-panel-nav input`
    ).forEach((r) => {
      r.checked = false;
    });

    this.querySelector(`.filters-panel .filters-panel-nav .range:first-child`).value = 0;
    this.querySelector(`[name="filter.v.price.gte"]`).value = 0;
    this.querySelector(`.filters-panel .filters-panel-nav .range:last-child`).value = Number(this.querySelector(`.filters-panel .filters-panel-nav .range:last-child`).getAttribute("max"));
    this.querySelector(`[name="filter.v.price.lte"]`).value = Number(this.querySelector(`.filters-panel .filters-panel-nav .range:last-child`).getAttribute("max"));

    this.url.search = ''; // Remove all search params
    this.searchParams = new URLSearchParams(); // Reset searchParams
    history.replaceState({}, null, this.url.toString());
    this.debouncedFilterProducts();
  }

  updateFilters() {
    this.url.search = this.searchParams.toString();
    history.replaceState({}, null, this.url.toString());
    this.debouncedFilterProducts();
  }

  filterProducts() {
    this.main?.classList.add("product-grid-loading");
    fetch(
      `${this.url.toString()}${this.url.toString().includes("?") ? "&" : "?"
      }view=none`
    )
      .then((response) => response.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        const productGridElement = doc.querySelector("#collectionProductGrid");
        const productCount =
          productGridElement && productGridElement.dataset.productCount
            ? parseInt(productGridElement.dataset.productCount, 10)
            : 0;

        if (productCount > 0) {
          this.updateProductGrid(doc);
          this.updateCollectionFooter(doc);
        } else {
          this.showNoProductsFound();
        }

        if(this.searchParams.size > 0){
          this.filtersClear.style.display = "flex";
        } else {
          this.filtersClear.style.display = "none";
        }

        this.updateFilterCheckboxes(doc);
        this.updateFilterCounts(doc);
        this.scrollToContent();
        this.main?.classList.remove("product-grid-loading");
      });
  }

  updateProductGrid(doc) {
    const productGridElement = doc.getElementById("collectionProductGrid");
    if (productGridElement) {
      const prodsHTML = productGridElement.innerHTML;
      const prodsCountNumber =
        productGridElement.getAttribute("data-product-count");
      this.productGrid.innerHTML = prodsHTML;
      this.productGridCountFilterBar.textContent = prodsCountNumber;
      if (this.filterDrawerButtonProductCount) {
        this.filterDrawerButtonProductCount.textContent = prodsCountNumber;
      }

      if (this.filterDrawerButton) {
        this.filterDrawerButton.textContent = `Filter & Sort (${prodsCountNumber})`;
      }
      if (this.hasRegistryFeature && this.customerId) {
        this.initializeRegistryItems();
      }
    }
  }

  updateCollectionFooter(doc) {
    const newCollectionFooter = doc.querySelector(".collection-footer");
    if (newCollectionFooter && this.collectionFooter) {
      this.collectionFooter.innerHTML = newCollectionFooter.innerHTML;
    }
  }

  showNoProductsFound() {
    if (this.productGrid) {
      this.productGrid.innerHTML = `
        <div class="not-found-top not-found-top--js">
          <h1>No Products Found</h1>
          <p>Use the search bar to find inspiration or select a category above.</p>
        </div>
      `;
      this.productGridCountFilterBar.textContent = "0";
      if (this.filterDrawerButtonProductCount) {
        this.filterDrawerButtonProductCount.textContent = "0";
      }
      // Clear the collection footer when no products are found
      if (this.collectionFooter) {
        this.collectionFooter.innerHTML = "";
      }
    }
  }

  updateFilterCheckboxes(doc) {
    doc
      .querySelectorAll(".filters-panel .filters-panel-nav input")
      .forEach((input) => {
        const id = input.id;
        if (id) {
          const localInput = this.querySelector(`#${id}`);
          if (localInput) {
            localInput.disabled = input.disabled;
            if(localInput.disabled){
              localInput.parentElement.classList.add("checkbox-container--disabled");
            } else {
              localInput.parentElement.classList.remove("checkbox-container--disabled");
            }
            if (input.type === "checkbox") {
              localInput.checked = input.checked;
            }
          }
        }
      });
  }

  updateFilterCounts(doc) {
    doc
      .querySelectorAll(".filters-panel .filters-panel-nav label")
      .forEach((label) => {
        const id = label.getAttribute("for");
        if (id) {
          const localLabel = this.querySelector(`label[for="${id}"]`);
          const localInput = this.querySelector(`#${id}`);
          if (localLabel && localInput) {
            const count = this.extractCount(label.innerHTML);
            localLabel.innerHTML = label.innerHTML;
            localInput.disabled = count === 0;
            localLabel.classList.toggle("filter-disabled", count === 0);
          }
        }
      });
  }

  extractCount(innerHTML) {
    const match = innerHTML.match(/\((\d+)\)/);
    return match ? parseInt(match[1], 10) : 0;
  }

  handlePriceRangeChange({ min, max }) {
    this.searchParams.delete("filter.v.price.gte");
    this.searchParams.delete("filter.v.price.lte");

    if (min) {
      this.searchParams.append("filter.v.price.gte", min);
    }
    if (max) {
      this.searchParams.append("filter.v.price.lte", max);
    }

    this.searchParams.delete("page");
    this.updateFilters();
  }
}

if (!window.customElements.get("collection-filter")) {
  window.customElements.define("collection-filter", CollectionFilter);
}

var PriceRange = class extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this.rangeLowerBound = this.querySelector(
      'input[type="range"]:first-child'
    );
    this.rangeHigherBound = this.querySelector(
      'input[type="range"]:last-child'
    );
    this.textInputLowerBound = this.querySelector(
      'input[name="filter.v.price.gte"]'
    );
    this.textInputHigherBound = this.querySelector(
      'input[name="filter.v.price.lte"]'
    );

    if (this.textInputLowerBound) {
      this.textInputLowerBound.addEventListener(
        "focus",
        () => this.textInputLowerBound.select(),
        { signal: this._abortController.signal }
      );
    }

    if (this.textInputHigherBound) {
      this.textInputHigherBound.addEventListener(
        "focus",
        () => this.textInputHigherBound.select(),
        { signal: this._abortController.signal }
      );
    }

    if (this.textInputLowerBound) {
      this.textInputLowerBound.addEventListener(
        "change",
        (event) => {
          event.preventDefault();
          event.target.value = Math.max(
            Math.min(
              parseInt(event.target.value),
              parseInt(this.textInputHigherBound?.value || event.target.max) - 1
            ),
            event.target.min
          );
          if (this.rangeLowerBound) {
            this.rangeLowerBound.value = event.target.value;
            this.rangeLowerBound.parentElement.style.setProperty(
              "--range-min",
              `${(parseInt(this.rangeLowerBound.value) /
                parseInt(this.rangeLowerBound.max)) *
              100
              }%`
            );
          }
        },
        { signal: this._abortController.signal }
      );
    }

    if (this.textInputHigherBound) {
      this.textInputHigherBound.addEventListener(
        "change",
        (event) => {
          event.preventDefault();
          event.target.value = Math.min(
            Math.max(
              parseInt(event.target.value),
              parseInt(this.textInputLowerBound?.value || event.target.min) + 1
            ),
            event.target.max
          );
          if (this.rangeHigherBound) {
            this.rangeHigherBound.value = event.target.value;
            this.rangeHigherBound.parentElement.style.setProperty(
              "--range-max",
              `${(parseInt(this.rangeHigherBound.value) /
                parseInt(this.rangeHigherBound.max)) *
              100
              }%`
            );
          }
        },
        { signal: this._abortController.signal }
      );
    }

    if (this.rangeLowerBound) {
      this.rangeLowerBound.addEventListener(
        "change",
        (event) => {
          event.stopPropagation();
          if (this.textInputLowerBound) {
            this.textInputLowerBound.value = event.target.value;
            this.textInputLowerBound.dispatchEvent(
              new Event("change", { bubbles: true })
            );
          }
        },
        { signal: this._abortController.signal }
      );
    }

    if (this.rangeHigherBound) {
      this.rangeHigherBound.addEventListener(
        "change",
        (event) => {
          event.stopPropagation();
          if (this.textInputHigherBound) {
            this.textInputHigherBound.value = event.target.value;
            this.textInputHigherBound.dispatchEvent(
              new Event("change", { bubbles: true })
            );
          }
        },
        { signal: this._abortController.signal }
      );
    }

    if (this.rangeLowerBound) {
      this.rangeLowerBound.addEventListener(
        "input",
        (event) => {
          event.target.value = Math.min(
            parseInt(event.target.value),
            parseInt(this.textInputHigherBound?.value || event.target.max) - 1
          );
          event.target.parentElement.style.setProperty(
            "--range-min",
            `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100
            }%`
          );
          if (this.textInputLowerBound) {
            this.textInputLowerBound.value = event.target.value;
          }
        },
        { signal: this._abortController.signal }
      );
    }

    if (this.rangeHigherBound) {
      this.rangeHigherBound.addEventListener(
        "input",
        (event) => {
          event.target.value = Math.max(
            parseInt(event.target.value),
            parseInt(this.textInputLowerBound?.value || event.target.min) + 1
          );
          event.target.parentElement.style.setProperty(
            "--range-max",
            `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100
            }%`
          );
          if (this.textInputHigherBound) {
            this.textInputHigherBound.value = event.target.value;
          }
        },
        { signal: this._abortController.signal }
      );
    }

    if (this.rangeLowerBound) {
      this.rangeLowerBound.addEventListener("change", () =>
        this.dispatchPriceRangeEvent()
      );
    }

    if (this.rangeHigherBound) {
      this.rangeHigherBound.addEventListener("change", () =>
        this.dispatchPriceRangeEvent()
      );
    }

    if (this.textInputLowerBound) {
      this.textInputLowerBound.addEventListener("change", () =>
        this.dispatchPriceRangeEvent()
      );
    }

    if (this.textInputHigherBound) {
      this.textInputHigherBound.addEventListener("change", () =>
        this.dispatchPriceRangeEvent()
      );
    }
  }

  dispatchPriceRangeEvent() {
    const event = new CustomEvent("price-range-change", {
      bubbles: true,
      detail: {
        min: this.textInputLowerBound?.value,
        max: this.textInputHigherBound?.value,
      },
    });
    this.dispatchEvent(event);
  }

  disconnectedCallback() {
    this._abortController.abort();
  }
};
if (!window.customElements.get("price-range")) {
  window.customElements.define("price-range", PriceRange);
}

class AutoWidthSelect extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector("select");
    if (!this.select) return;

    this.measureSpan = document.createElement("span");
    Object.assign(this.measureSpan.style, {
      position: "absolute",
      visibility: "hidden",
      whiteSpace: "nowrap",
      font: window.getComputedStyle(this.select).font, // Match font
      padding: window.getComputedStyle(this.select).padding, // Match padding
    });

    document.body.appendChild(this.measureSpan);

    this.updateWidth();
    this.select.addEventListener("change", () => this.updateWidth());
  }

  updateWidth() {
    this.measureSpan.textContent = this.select.options[this.select.selectedIndex].text;
    const padding = parseFloat(window.getComputedStyle(this.select).paddingLeft) +
                    parseFloat(window.getComputedStyle(this.select).paddingRight);
    this.select.style.width = `${this.measureSpan.offsetWidth + padding - 3}px`; // Extra buffer
  }
}

customElements.define("auto-width-select", AutoWidthSelect);